import { useState } from "react";
import axios from "axios";
import { motion, AnimatePresence } from "framer-motion";
import { Zap, Loader2, AlertCircle, CheckCircle2, XCircle, ArrowRight } from "lucide-react";
import { toast } from "sonner";
import { fadeUp, viewport } from "@/lib/motion";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

const statusIcon = { good: CheckCircle2, warn: AlertCircle, bad: XCircle };
const statusColor = { good: "text-[#ccff00]", warn: "text-amber-400", bad: "text-[#ff0055]" };

const go = (href) => document.querySelector(href)?.scrollIntoView({ behavior: "smooth" });

const WebsiteScore = () => {
  const [url, setUrl] = useState("");
  const [desc, setDesc] = useState("");
  const [loading, setLoading] = useState(false);
  const [result, setResult] = useState(null);

  const submit = async (e) => {
    e.preventDefault();
    if (!url && !desc) {
      toast.error("Enter your website URL or describe your business.");
      return;
    }
    setLoading(true);
    setResult(null);
    try {
      const { data } = await axios.post(`${API}/website-score`, { url, description: desc });
      setResult(data);
    } catch (err) {
      toast.error(err?.response?.data?.detail || "Something went wrong. Please try again.");
    } finally {
      setLoading(false);
    }
  };

  return (
    <section id="score" className="py-24 md:py-32 bg-[#0a0a0a] border-y border-white/10" data-testid="website-score">
      <div className="max-w-5xl mx-auto px-5 md:px-8">
        <motion.div variants={fadeUp} initial="hidden" whileInView="show" viewport={viewport} className="text-center mb-12">
          <div className="inline-flex items-center gap-2 rounded-full bg-[#ccff00]/15 border border-[#ccff00]/40 text-[#ccff00] text-xs font-semibold uppercase tracking-widest px-4 py-2 mb-6">
            <Zap size={14} /> Free lead magnet
          </div>
          <h2 className="font-display font-black tracking-tighter text-4xl md:text-5xl lg:text-6xl leading-tight mb-4">
            How good is your website?
          </h2>
          <p className="text-lg text-zinc-400 max-w-2xl mx-auto">
            Get an instant AI-powered Website Score out of 100 — with the exact things holding your business back.
          </p>
        </motion.div>

        <motion.form variants={fadeUp} initial="hidden" whileInView="show" viewport={viewport} onSubmit={submit} className="bg-[#121212] border border-white/10 rounded-2xl p-6 md:p-8" data-testid="score-form">
          <div className="grid md:grid-cols-2 gap-4 mb-4">
            <input
              type="text"
              value={url}
              onChange={(e) => setUrl(e.target.value)}
              placeholder="yourbusiness.co.uk"
              className="w-full bg-[#0a0a0a] border border-white/10 rounded-xl px-5 py-4 text-white placeholder:text-zinc-600 focus:ring-2 focus:ring-[#ccff00] focus:border-transparent outline-none transition-shadow"
              data-testid="score-url-input"
            />
            <input
              type="text"
              value={desc}
              onChange={(e) => setDesc(e.target.value)}
              placeholder="…or describe your business"
              className="w-full bg-[#0a0a0a] border border-white/10 rounded-xl px-5 py-4 text-white placeholder:text-zinc-600 focus:ring-2 focus:ring-[#ccff00] focus:border-transparent outline-none transition-shadow"
              data-testid="score-desc-input"
            />
          </div>
          <button type="submit" disabled={loading} className="w-full md:w-auto inline-flex items-center justify-center gap-2 rounded-full bg-[#ccff00] text-black font-bold uppercase tracking-widest text-sm px-8 py-4 hover:scale-[1.02] transition-transform disabled:opacity-60" data-testid="score-submit">
            {loading ? <><Loader2 size={18} className="animate-spin" /> Analysing…</> : <><Zap size={18} /> Get my Website Score</>}
          </button>
        </motion.form>

        <AnimatePresence>
          {result && (
            <motion.div initial={{ opacity: 0, y: 30 }} animate={{ opacity: 1, y: 0 }} className="mt-8 bg-[#121212] border border-white/10 rounded-2xl p-6 md:p-10" data-testid="score-result">
              <div className="flex flex-col md:flex-row gap-8 items-center md:items-start mb-10">
                <ScoreGauge value={result.overall_score} />
                <div className="flex-1 text-center md:text-left">
                  <p className="text-xs uppercase tracking-widest text-zinc-500 mb-2">The verdict</p>
                  <p className="font-display font-bold text-2xl md:text-3xl leading-tight mb-4" data-testid="score-verdict">{result.verdict}</p>
                  {result.recommended_package && (
                    <p className="text-zinc-400">Recommended fix: <span className="text-[#ccff00] font-semibold">{result.recommended_package}</span> package</p>
                  )}
                </div>
              </div>

              <div className="grid sm:grid-cols-2 gap-3 mb-8">
                {(result.categories || []).map((c, i) => {
                  const Icon = statusIcon[c.status] || AlertCircle;
                  return (
                    <div key={i} className="flex items-start gap-3 bg-[#0a0a0a] border border-white/10 rounded-xl p-4" data-testid={`score-category-${i}`}>
                      <Icon size={20} className={`${statusColor[c.status] || "text-zinc-400"} shrink-0 mt-0.5`} />
                      <div className="flex-1">
                        <div className="flex justify-between items-center">
                          <span className="font-medium text-white text-sm">{c.name}</span>
                          <span className="text-sm text-zinc-500">{c.score}/100</span>
                        </div>
                        {c.note && <p className="text-xs text-zinc-500 mt-1">{c.note}</p>}
                      </div>
                    </div>
                  );
                })}
              </div>

              {result.quick_wins?.length > 0 && (
                <div className="mb-8">
                  <p className="text-xs uppercase tracking-widest text-zinc-500 mb-3">Quick wins</p>
                  <ul className="grid sm:grid-cols-2 gap-2">
                    {result.quick_wins.map((w, i) => (
                      <li key={i} className="flex items-start gap-2 text-sm text-zinc-300"><ArrowRight size={15} className="text-[#ccff00] mt-1 shrink-0" />{w}</li>
                    ))}
                  </ul>
                </div>
              )}

              <div className="rounded-xl bg-[#ccff00] text-black p-6 flex flex-col sm:flex-row items-center justify-between gap-4">
                <p className="font-display font-bold text-lg">Want us to fix all of this — in 48 hours?</p>
                <button onClick={() => go("#contact")} className="shrink-0 rounded-full bg-black text-[#ccff00] font-bold uppercase tracking-widest text-xs px-6 py-3.5 hover:scale-105 transition-transform" data-testid="score-fix-cta">
                  Fix my website from £199
                </button>
              </div>
            </motion.div>
          )}
        </AnimatePresence>
      </div>
    </section>
  );
};

const ScoreGauge = ({ value }) => {
  const v = Math.max(0, Math.min(100, value || 0));
  const color = v >= 70 ? "#ccff00" : v >= 40 ? "#fbbf24" : "#ff0055";
  const circ = 2 * Math.PI * 52;
  return (
    <div className="relative w-40 h-40 shrink-0" data-testid="score-gauge">
      <svg className="w-full h-full -rotate-90" viewBox="0 0 120 120">
        <circle cx="60" cy="60" r="52" fill="none" stroke="rgba(255,255,255,0.08)" strokeWidth="10" />
        <motion.circle
          cx="60" cy="60" r="52" fill="none" stroke={color} strokeWidth="10" strokeLinecap="round"
          strokeDasharray={circ}
          initial={{ strokeDashoffset: circ }}
          animate={{ strokeDashoffset: circ - (circ * v) / 100 }}
          transition={{ duration: 1.2, ease: "easeOut" }}
        />
      </svg>
      <div className="absolute inset-0 flex flex-col items-center justify-center">
        <span className="font-display font-black text-4xl" style={{ color }}>{v}</span>
        <span className="text-xs text-zinc-500 uppercase tracking-widest">/ 100</span>
      </div>
    </div>
  );
};

export default WebsiteScore;
